// lib/image-export.ts
import type { OriginalImage, OutputImageFormat } from '#types/types';
import { isLayerVectorized } from '#lib/svg-layer';

const TOP_LAYER_RENDER_ORDER_OFFSET = 1_000_000;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Visible, rasterizable layers of a workspace, in painting order (render order,
 * "always above" layers last, ties broken by id).
 */
export const collectVisibleImageExportLayers = <TLayer extends {
  id: string;
  type?: string | null;
  visible?: boolean | null;
  renderOrder?: number | null;
}>(
  layerIds: string[],
  layerRegistry: Record<string, TLayer>
): TLayer[] => (
  (Array.isArray(layerIds) ? layerIds : [])
    .map((layerId, index) => {
      const layer = layerRegistry[layerId];
      if (!layer || layer.visible === false) return null;
      if (layer.type === 'video' || layer.type === 'audio') return null;
      const renderOrder = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
        ? layer.renderOrder
        : index;
      const isTopLayer = Reflect.get(layer, 'top-layer') === true || Reflect.get(layer, 'topLayer') === true;
      return {
        layer,
        renderOrder: isTopLayer ? renderOrder + TOP_LAYER_RENDER_ORDER_OFFSET : renderOrder,
      };
    })
    .filter((entry): entry is { layer: TLayer; renderOrder: number } => Boolean(entry))
    .sort((left, right) => {
      if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
      return left.layer.id.localeCompare(right.layer.id);
    })
    .map((entry) => entry.layer)
);

/** True when the workspace holds at least one visible video/audio timeline track. */
export const hasVisibleTimelineMedia = (
  layerIds: string[],
  layerRegistry: Record<string, {
    type?: string | null;
    visible?: boolean | null;
    videoSrc?: string | null;
  }>
): boolean => (
  (Array.isArray(layerIds) ? layerIds : []).some((layerId) => {
    const layer = layerRegistry[layerId];
    if (!layer || layer.visible === false) return false;
    if (layer.type !== 'video' && layer.type !== 'audio') return false;
    return typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0;
  })
);

/** Native SVG export needs every visible image layer to already be vector. */
export const isNativeSvgExportAvailable = ({
  isSourceImageMode,
  hasTimelineMedia,
  includeAnnotations,
  visibleImageExportLayers,
}: {
  isSourceImageMode: boolean;
  hasTimelineMedia: boolean;
  includeAnnotations: boolean;
  visibleImageExportLayers: Array<{ type?: string | null; params?: unknown; imageSrc?: string | null }>;
}): boolean => (
  !isSourceImageMode &&
  !hasTimelineMedia &&
  !includeAnnotations &&
  visibleImageExportLayers.length > 0 &&
  visibleImageExportLayers.every((layer) => isLayerVectorized(layer))
);

const loadImageFromBlob = async (blob: Blob): Promise<HTMLImageElement> => {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = 'async';
    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to decode export image.'));
    });
    image.src = objectUrl;
    return await loadPromise;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error(`Failed to create ${mimeType} export blob.`));
    }, mimeType, quality);
  });

const formatPdfNumber = (value: number): string => {
  const rounded = Math.round(Math.max(1, value) * 1000) / 1000;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

const buildSingleImagePdfBlob = (
  jpegBytes: Uint8Array,
  width: number,
  height: number,
): Blob => {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [0];
  let byteOffset = 0;

  const pushBytes = (bytes: Uint8Array) => {
    chunks.push(bytes);
    byteOffset += bytes.length;
  };
  const pushText = (text: string) => pushBytes(encoder.encode(text));
  const addObject = (id: number, body: string) => {
    offsets[id] = byteOffset;
    pushText(`${id} 0 obj\n${body}\nendobj\n`);
  };

  const pdfWidth = formatPdfNumber(width);
  const pdfHeight = formatPdfNumber(height);
  const content = `q\n${pdfWidth} 0 0 ${pdfHeight} 0 0 cm\n/Im0 Do\nQ\n`;
  const contentLength = encoder.encode(content).length;

  pushText('%PDF-1.4\n');
  addObject(1, '<< /Type /Catalog /Pages 2 0 R >>');
  addObject(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  addObject(
    3,
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pdfWidth} ${pdfHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`
  );

  offsets[4] = byteOffset;
  pushText(
    `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${Math.max(1, Math.round(width))} /Height ${Math.max(1, Math.round(height))} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`
  );
  pushBytes(jpegBytes);
  pushText('\nendstream\nendobj\n');

  offsets[5] = byteOffset;
  pushText(`5 0 obj\n<< /Length ${contentLength} >>\nstream\n${content}endstream\nendobj\n`);

  const xrefOffset = byteOffset;
  const xrefEntries = Array.from({ length: 5 }, (_, index) => {
    const offset = offsets[index + 1] ?? 0;
    return `${String(offset).padStart(10, '0')} 00000 n \n`;
  }).join('');
  pushText(`xref\n0 6\n0000000000 65535 f \n${xrefEntries}trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`);

  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(totalLength);
  let writeOffset = 0;
  chunks.forEach((chunk) => {
    output.set(chunk, writeOffset);
    writeOffset += chunk.length;
  });
  return new Blob([output], { type: 'application/pdf' });
};

/** Flattens a raster export onto a single-page PDF. */
export const createPdfExportFile = async (
  rasterFile: Blob,
  width: number,
  height: number,
  quality: number,
): Promise<File> => {
  const safeWidth = Math.max(1, Math.round(width));
  const safeHeight = Math.max(1, Math.round(height));
  const image = await loadImageFromBlob(rasterFile);
  const canvas = document.createElement('canvas');
  canvas.width = safeWidth;
  canvas.height = safeHeight;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) {
    throw new Error('PDF export canvas is unavailable.');
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, safeWidth, safeHeight);
  context.drawImage(image, 0, 0, safeWidth, safeHeight);
  const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', clamp(quality, 0.05, 1));
  const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());
  const pdfBlob = buildSingleImagePdfBlob(jpegBytes, safeWidth, safeHeight);
  return new File([pdfBlob], 'background.pdf', { type: 'application/pdf' });
};

/**
 * SOURCE-IMAGE mode: scale the asset to the requested pixel budget and encode
 * it with the SAME format/quality/alpha controls as canvas exports.
 */
export const renderSourceImageExport = async (
  sourceImageUrl: string,
  targetPixels: number,
  rasterFormat: OutputImageFormat,
  quality: number | undefined,
  alpha: boolean,
): Promise<OriginalImage> => {
  const response = await fetch(sourceImageUrl);
  if (!response.ok) {
    throw new Error(`Failed to load the source image (${response.status}).`);
  }
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  const sourceWidth = bitmap.width;
  const sourceHeight = Math.max(1, bitmap.height);
  const aspect = sourceWidth / sourceHeight;
  const targetHeight = Math.max(1, Math.round(Math.sqrt(targetPixels / aspect)));
  const targetWidth = Math.max(1, Math.round(targetHeight * aspect));
  const canvasElement = document.createElement('canvas');
  canvasElement.width = targetWidth;
  canvasElement.height = targetHeight;
  const context = canvasElement.getContext('2d');
  if (!context) {
    if (typeof bitmap.close === 'function') bitmap.close();
    throw new Error('Canvas 2D context unavailable for export.');
  }
  if (!alpha) {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, targetWidth, targetHeight);
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
  if (typeof bitmap.close === 'function') bitmap.close();
  const mimeType = rasterFormat === 'png' ? 'image/png' : rasterFormat === 'webp' ? 'image/webp' : 'image/jpeg';
  const encodedBlob: Blob = await new Promise((resolve, reject) => {
    canvasElement.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('Failed to encode the export.'))),
      mimeType,
      quality,
    );
  });
  return {
    file: new File([encodedBlob], `output-export.${rasterFormat}`, { type: mimeType }),
    upscaleFactor: 1,
    width: sourceWidth,
    height: sourceHeight,
    targetWidth,
    targetHeight,
    imageData: null,
  };
};
